import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AgingBucket, CustomerListRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import { ReportSearchField, ReportToolbar } from '@/components/ReportToolbar';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { TablePager } from '@/components/TablePager';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchCustomers, portalKeys } from '@/features/portal/api';
import { AGING_FILTERS, AgingText } from '@/features/portal/aging';
import { formatCount, formatMoney } from '@/lib/format';

const PAGE_SIZE = 15;

/**
 * Every customer this tenant has, across all their branches.
 *
 * Deliberately NOT date-filtered. The other reports answer "what happened in
 * this period"; this answers "who is this person and what have they ever
 * bought", and a range would quietly hide most of the answer.
 */
export function CustomersPage() {
  const { currency } = usePortal();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Which "last visited" bucket to show; 'all' shows everyone.
  const [aging, setAging] = useState<'all' | AgingBucket>('all');

  const query = useQuery({
    queryKey: portalKeys.customers(),
    queryFn: ({ signal }) => fetchCustomers(signal),
  });

  const columns = useMemo<Column<CustomerListRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        cell: (row) => (
          <Link className="link-primary font-medium" to={`/portal/customers/${encodeURIComponent(row.key)}`}>
            {row.name || '—'}
          </Link>
        ),
      },
      { key: 'phone', header: 'Phone', cell: (row) => row.phone || '—' },
      {
        key: 'branches',
        header: 'Branches',
        cell: (row) => (row.branches.length ? row.branches.join(', ') : '—'),
      },
      {
        key: 'spent',
        header: 'Spent',
        align: 'right',
        cell: (row) => formatMoney(row.spent, currency),
      },
      {
        key: 'outstanding',
        header: 'Owes now',
        align: 'right',
        cell: (row) =>
          Number(row.outstanding) > 0.005 ? (
            <span className="text-amber-700">{formatMoney(row.outstanding, currency)}</span>
          ) : (
            formatMoney(row.outstanding, currency)
          ),
      },
      {
        key: 'points',
        header: 'Points',
        align: 'right',
        cell: (row) => formatCount(row.points),
      },
      {
        key: 'lastVisit',
        header: 'Last visit',
        cell: (row) => row.lastVisit ?? '—',
      },
      {
        // How many times they bought: one per bill.
        key: 'visits',
        header: 'Visits',
        align: 'right',
        cell: (row) => formatCount(row.visits),
      },
      {
        key: 'aging',
        header: 'Aging',
        cell: (row) => <AgingText days={row.agingDays} bucket={row.agingBucket} />,
      },
    ],
    [currency],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (query.data?.rows ?? []).filter(
      (r) => aging === 'all' || r.agingBucket === aging,
    );
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.phone.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q),
    );
  }, [query.data, search, aging]);

  // How many customers sit in each bucket, so a chip says what it will show.
  const bucketCounts = useMemo(() => {
    const counts: Record<string, number> = { all: query.data?.rows.length ?? 0 };
    for (const r of query.data?.rows ?? []) {
      if (r.agingBucket) counts[r.agingBucket] = (counts[r.agingBucket] ?? 0) + 1;
    }
    return counts;
  }, [query.data]);

  const slice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <PageHeader
        title="Customers"
        description="Everyone who has been billed by name, across every branch. A customer with a phone is one row wherever they shopped."
      />

      <div className="space-y-6">
        <AsyncSection
          state={query}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 3 }).map((_, i) => (
                <KpiTileSkeleton key={i} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile label="Customers" value={formatCount(data.count)} />
              {/* Walk-ins are shown, not hidden: without this a shop
                  reconciles the list against its sales report and finds a gap
                  it cannot explain. */}
              <KpiTile label="Walk-in bills" value={formatCount(data.walkInOrders)} />
              <KpiTile
                label="Walk-in spend"
                value={formatMoney(data.walkInSpend, data.currency || currency)}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        <ReportToolbar>
          <ReportSearchField
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search name, phone or email…"
          />
        </ReportToolbar>

        {/* When did they last buy? Narrow to customers going quiet. */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by last visit">
          {AGING_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={aging === f.id}
              onClick={() => {
                setAging(f.id);
                setPage(1);
              }}
              className={
                aging === f.id
                  ? 'rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white'
                  : 'rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900'
              }
            >
              {f.label}
              <span className="ml-1.5 tabular-nums opacity-70">{bucketCounts[f.id] ?? 0}</span>
            </button>
          ))}
        </div>

        <AsyncSection
          state={query}
          skeleton={<TableSkeleton rows={8} columns={8} />}
          isEmpty={() => filtered.length === 0}
          emptyTitle="No customers"
          emptyDescription="Only sales billed to a named customer appear here. Walk-in sales belong to nobody."
        >
          {() => (
            <>
              <DataTable
                caption="Customers, with what each has spent and still owes"
                columns={columns}
                rows={slice}
                rowKey={(r) => r.key}
              />
              <TablePager
                page={page}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                onPageChange={setPage}
              />
            </>
          )}
        </AsyncSection>
      </div>
    </>
  );
}
